import * as React from 'react';
import {
  TPAComponentsWrapper,
  createRendererWithUniDriver,
} from '../../test/utils';
import { ButtonType, Pagination, PaginationProps } from '.';
import { StylableDOMUtil } from '@stylable/dom-test-kit';
import { paginationDriverFactory } from './Pagination.driver';
import * as style from './Pagination.st.css';

const stylableUtil = new StylableDOMUtil(style);

describe('Pagination', () => {
  const TestPagination = (props: PaginationProps) => (
    <Pagination {...props} data-hook="pagination-component" />
  );
  const createDriver = createRendererWithUniDriver(paginationDriverFactory);

  // TODO: JB: use jest-location-mock instead of patching console.error
  let consoleError: typeof console.error;
  let consoleErrorSpy: jest.SpyInstance;

  beforeAll(() => {
    consoleError = console.error;
    consoleErrorSpy = jest
      .spyOn(console, 'error')
      .mockImplementation((...args) => {
        const message = args[0];
        if (
          typeof message === 'string' &&
          message.includes(
            'Error: Not implemented: navigation (except hash changes)',
          )
        ) {
          return;
        }
        consoleError.apply(console, args);
      });
  });

  afterAll(() => {
    consoleErrorSpy.mockRestore();
  });

  describe('Pagination tests', () => {
    it('should render', async () => {
      const { driver } = createDriver(<TestPagination totalPages={10} />);

      expect(await driver.exists()).toBe(true);
      expect(await driver.isMobile()).toBe(null);
    });

    it('should use mobile design', async () => {
      const { driver } = createDriver(
        TPAComponentsWrapper({ mobile: true })(
          <TestPagination totalPages={10} />,
        ),
      );

      expect(await driver.isMobile()).toBe('true');
    });

    it('Should show first & last navigation buttons when total pages count more than maxPagesToShow', async () => {
      const { driver } = createDriver(
        <TestPagination totalPages={10} maxPagesToShow={5} />,
      );
      expect(await driver.getNavButton(ButtonType.First).exists()).toBe(true);
      expect(await driver.getNavButton(ButtonType.Last).exists()).toBe(true);
    });

    it('Should hide first & last navigation buttons when total pages count less or equal than maxPagesToShow', async () => {
      const { driver } = createDriver(
        <TestPagination totalPages={5} maxPagesToShow={5} />,
      );
      expect(await driver.getNavButton(ButtonType.First).exists()).toBe(false);
      expect(await driver.getNavButton(ButtonType.Last).exists()).toBe(false);
    });

    it('Should not render page input in desktop mode', async () => {
      const { driver } = createDriver(
        TPAComponentsWrapper({ mobile: false })(
          <TestPagination totalPages={10} />,
        ),
      );
      expect(await driver.getPageInput().exists()).toBe(false);
    });

    it('Should not render page input in mobile mode', async () => {
      const { driver } = createDriver(
        TPAComponentsWrapper({ mobile: true })(
          <TestPagination totalPages={10} />,
        ),
      );

      expect(await driver.getPageInput().exists()).toBe(false);
    });

    it('Should not render more than 5 pages by default', async () => {
      const { driver } = createDriver(<TestPagination totalPages={10} />);
      expect((await driver.getPageElements()).length).toBe(5);
    });
  });

  describe('Accessibility', () => {
    it('has <nav> as the root node', async () => {
      const { driver } = createDriver(<TestPagination totalPages={5} />);
      expect((await driver.element()).tagName).toEqual('NAV');
    });
    it('has aria-label', async () => {
      const { driver } = createDriver(<TestPagination totalPages={5} />);

      expect((await driver.element()).getAttribute('aria-label')).toEqual(
        'Pagination',
      );
    });
    it('has correct order of elements for screen readers', async () => {
      const { driver } = createDriver(
        <TestPagination totalPages={5} showFirstLastNavButtons />,
      );

      const { firstButton, previousButton, nextButton, lastButton } =
        driver.getNavButtons();
      const pageStrip = driver.getPageStrip();

      expect(
        (await firstButton.getNative()).compareDocumentPosition(
          await previousButton.getNative(),
        ),
      ).toEqual(Node.DOCUMENT_POSITION_FOLLOWING);
      expect(
        (await previousButton.getNative()).compareDocumentPosition(
          await pageStrip.getNative(),
        ),
      ).toEqual(Node.DOCUMENT_POSITION_FOLLOWING);
      expect(
        (await pageStrip.getNative()).compareDocumentPosition(
          await nextButton.getNative(),
        ),
      ).toEqual(Node.DOCUMENT_POSITION_FOLLOWING);
      expect(
        (await nextButton.getNative()).compareDocumentPosition(
          await lastButton.getNative(),
        ),
      ).toEqual(Node.DOCUMENT_POSITION_FOLLOWING);
    });
    it('has aria-label attribute on pages', async () => {
      const { driver } = createDriver(<TestPagination totalPages={3} />);

      expect(await driver.getPageByNumber(2).attr('aria-label')).toEqual(
        'Page 2',
      );
      expect(await driver.getPageByNumber(3).attr('aria-label')).toEqual(
        'Page 3',
      );
    });
    it('has aria-label on the input field', async () => {
      const { driver } = createDriver(
        <TestPagination paginationMode="input" totalPages={5} />,
      );

      expect(await driver.getPageInput().attr('aria-label')).toEqual(
        'Page number, select a number between 1 and 5',
      );
    });
  });
  describe('Page numbers mode', () => {
    it('selects the first page by default', async () => {
      const { driver } = createDriver(<TestPagination totalPages={5} />);
      expect(await driver.getCurrentPage().text()).toBe('1');
    });
    it('selected page parent has aria-current', async () => {
      const { driver } = createDriver(<TestPagination totalPages={5} />);
      expect(
        await (
          await driver.getCurrentPage().getNative()
        ).parentElement.getAttribute('aria-current'),
      ).toBe('page');
    });
    it('selects given currentPage', async () => {
      const { driver } = createDriver(
        <TestPagination totalPages={5} currentPage={5} />,
      );

      expect(await driver.getCurrentPage().text()).toBe('5');
    });
    it('calls onChange on page click', async () => {
      const onChange = jest.fn();
      const { driver } = createDriver(
        <TestPagination
          totalPages={5}
          currentPage={1}
          onChange={onChange}
          pageUrl={(page) => `${page}`}
        />,
      );
      await driver.getPageByNumber(3).click();
      expect(onChange).toHaveBeenCalledWith(
        expect.objectContaining({ page: 3 }),
      );
    });
    it('does not call onChange on current page click', async () => {
      const onChange = jest.fn();
      const { driver } = createDriver(
        <TestPagination totalPages={5} currentPage={1} onChange={onChange} />,
      );
      await driver.getPageByNumber(1).click();

      expect(onChange).not.toBeCalled();
    });
    it('does not call onChange when disabled', async () => {
      const onChange = jest.fn();
      const { driver } = createDriver(
        <TestPagination
          totalPages={5}
          currentPage={1}
          onChange={onChange}
          showFirstLastNavButtons
          disabled
        />,
      );

      const { firstButton, previousButton, nextButton, lastButton } =
        driver.getNavButtons();

      const secondPage = driver.getPageByNumber(2);

      await firstButton.click();
      await previousButton.click();
      await nextButton.click();
      await lastButton.click();
      await secondPage.click();

      expect(onChange).not.toBeCalled();
    });
  });
  describe('Compact mode', () => {
    it('shows correct compact pages', async () => {
      const { driver } = createDriver(
        <TestPagination paginationMode="compact" totalPages={5} />,
      );
      expect(await driver.getCompactPages()).toBe('1 / 5');
    });
    it('returns the total page count', async () => {
      const { driver } = createDriver(
        <TestPagination paginationMode="compact" totalPages={5} />,
      );
      expect(await driver.getTotalPages()).toBe(5);
    });
  });
  describe('Input field mode', () => {
    it('shows the input field with the current page', async () => {
      const { driver } = createDriver(
        <TestPagination paginationMode="input" totalPages={5} />,
      );
      expect(await driver.getPageInput().value()).toEqual('1');
    });
    it('shows set aria label on input if provided', async () => {
      const ariaLabel = 'custom aria label';
      const { driver } = createDriver(
        <TestPagination
          inputAriaLabel={ariaLabel}
          paginationMode="input"
          totalPages={5}
        />,
      );
      expect(await driver.getPageInput().attr('aria-label')).toEqual(ariaLabel);
    });
    it('does not show the total amount of pages by default', async () => {
      const { driver } = createDriver(
        <TestPagination paginationMode="input" totalPages={5} />,
      );
      expect(await driver.getTotalPagesLabel().exists()).toBe(false);
    });
    it('shows the total amount of pages if showInputModeTotalPages=true', async () => {
      const { driver } = createDriver(
        <TestPagination
          paginationMode="input"
          totalPages={5}
          showInputModeTotalPages
        />,
      );
      expect(await driver.getTotalPagesLabel().text()).toEqual('5');
    });
    it('calls onChange on Enter press', async () => {
      const onChange = jest.fn();
      const { driver } = createDriver(
        <TestPagination
          paginationMode="input"
          totalPages={5}
          onChange={onChange}
        />,
      );
      await driver.changeInput('5');
      await driver.commitInput();

      expect(onChange).toHaveBeenCalledWith(
        expect.objectContaining({ page: 5 }),
      );
    });
    it('does not call onChange with an empty value', async () => {
      const onChange = jest.fn();
      const { driver } = createDriver(
        <TestPagination
          paginationMode="input"
          totalPages={5}
          onChange={onChange}
        />,
      );

      await driver.changeInput('');
      await driver.commitInput();

      expect(onChange).not.toBeCalled();
    });
    it('does not call onChange with an out-of-range value', async () => {
      const onChange = jest.fn();
      const { driver } = createDriver(
        <TestPagination
          paginationMode="input"
          totalPages={5}
          onChange={onChange}
        />,
      );

      await driver.changeInput('6');
      await driver.commitInput();

      expect(onChange).not.toBeCalled();
    });

    it('does not call onChange with the current page number', async () => {
      const onChange = jest.fn();
      const { driver } = createDriver(
        <TestPagination
          paginationMode="input"
          totalPages={5}
          currentPage={1}
          onChange={onChange}
        />,
      );

      await driver.changeInput('1');
      await driver.commitInput();

      expect(onChange).not.toBeCalled();
    });

    it('does not call onChange with a non-numeric value', async () => {
      const onChange = jest.fn();
      const { driver } = createDriver(
        <TestPagination
          paginationMode="input"
          totalPages={5}
          currentPage={1}
          onChange={onChange}
        />,
      );

      await driver.changeInput('five');
      await driver.commitInput();

      expect(onChange).not.toBeCalled();
    });
    it('reverts cleared input to the current page on blur', async () => {
      const { driver } = createDriver(
        <TestPagination
          paginationMode="input"
          totalPages={5}
          currentPage={1}
        />,
      );
      const input = driver.getPageInput();

      await driver.changeInput('');
      expect(await input.value()).toBe('');
      await input.blur();
      expect(await input.value()).toBe('1');
    });
    it('disables the input in disabled mode', async () => {
      const { driver } = createDriver(
        <TestPagination paginationMode="input" totalPages={5} disabled />,
      );
      expect(await driver.getPageInput()._prop('disabled')).toBe(true);
    });
    it(
      'adds error state after pressing Enter if the input has invalid value ' +
        'and removes the error state on entry',
      async () => {
        const { driver } = createDriver(
          <TestPagination paginationMode="input" totalPages={5} />,
        );
        const element = await driver.element();
        await driver.changeInput('6');
        await driver.commitInput();
        expect(stylableUtil.hasStyleState(element, 'error')).toBe(true);
        await driver.changeInput('1');
        expect(stylableUtil.hasStyleState(element, 'error')).toBe(false);
      },
    );
  });
  describe('Navigation buttons', () => {
    it('shows only next and previous buttons by default', async () => {
      const { driver } = createDriver(<TestPagination totalPages={3} />);

      expect(await driver.getNavButton(ButtonType.Prev).exists()).toBe(true);
      expect(await driver.getNavButton(ButtonType.Next).exists()).toBe(true);
      expect(await driver.getNavButton(ButtonType.First).exists()).toBe(false);
      expect(await driver.getNavButton(ButtonType.Last).exists()).toBe(false);
    });
    it('shows first and last buttons when showFirstLastNavButtons is on', async () => {
      const { driver } = createDriver(
        <TestPagination totalPages={3} showFirstLastNavButtons />,
      );

      const { firstButton, lastButton } = await driver.getNavButtons();
      expect(firstButton).toBeTruthy();
      expect(lastButton).toBeTruthy();
    });
    it('each of the buttons triggers onChange', async () => {
      const onChange = jest.fn();
      const { driver } = createDriver(
        <TestPagination
          totalPages={5}
          currentPage={3}
          showFirstLastNavButtons
          onChange={onChange}
          pageUrl={(page) => `${page}`}
        />,
      );

      const { firstButton, previousButton, nextButton, lastButton } =
        driver.getNavButtons();
      await previousButton.click();
      expect(onChange).lastCalledWith(expect.objectContaining({ page: 2 }));
      await nextButton.click();
      expect(onChange).lastCalledWith(expect.objectContaining({ page: 4 }));
      await firstButton.click();
      expect(onChange).lastCalledWith(expect.objectContaining({ page: 1 }));
      await lastButton.click();
      expect(onChange).lastCalledWith(expect.objectContaining({ page: 5 }));
    });
    it('disables first and previous buttons when on the first page', async () => {
      const onChange = jest.fn();
      const { driver } = createDriver(
        <TestPagination
          totalPages={5}
          currentPage={1}
          showFirstLastNavButtons
        />,
      );
      const { firstButton, previousButton } = driver.getNavButtons();
      await previousButton.click();
      await firstButton.click();

      expect(onChange).not.toBeCalled();
    });
    it('disables next and last buttons when on the last page', async () => {
      const onChange = jest.fn();
      const { driver } = createDriver(
        <TestPagination
          totalPages={5}
          currentPage={5}
          showFirstLastNavButtons
        />,
      );

      const { nextButton, lastButton } = driver.getNavButtons();

      await nextButton.click();
      await lastButton.click();

      expect(onChange).not.toBeCalled();
    });
    it('allows to customize button text', async () => {
      const { driver } = createDriver(
        <TestPagination
          totalPages={5}
          showFirstLastNavButtons
          firstLabel="first-label"
          previousLabel="previous-label"
          nextLabel="next-label"
          lastLabel="last-label"
        />,
      );

      const { firstButton, previousButton, nextButton, lastButton } =
        driver.getNavButtons();
      expect(await firstButton.text()).toEqual('first-label');
      expect(await previousButton.text()).toEqual('previous-label');
      expect(await nextButton.text()).toEqual('next-label');
      expect(await lastButton.text()).toEqual('last-label');
    });
    it('renders previous and next buttons as empty when disabled', async () => {
      const { driver } = createDriver(
        <TestPagination
          totalPages={5}
          showNextLabel={false}
          showPreviousLabel={false}
        />,
      );
      const nextButton = driver.getNavButton(ButtonType.Next);
      const previousButton = driver.getNavButton(ButtonType.Prev);

      expect(await nextButton.text()).toBe('');
      expect(await previousButton.text()).toBe('');
    });
  });
  describe('Keyboard and mouse interaction', () => {
    it('allows to select items Enter', async () => {
      const onChange = jest.fn();
      const { driver } = createDriver(
        <TestPagination
          totalPages={3}
          currentPage={1}
          onChange={onChange}
          pageUrl={(page) => `${page}`}
        />,
      );

      const nextButton = driver.getNavButton(ButtonType.Next);
      const secondPage = driver.getPageByNumber(2);

      await nextButton.pressKey('j');
      await secondPage.pressKey('k');

      expect(onChange).not.toBeCalled();

      // Pressing Enter on anchor element triggers click event
      await secondPage.click();
      expect(onChange).toHaveBeenCalledTimes(1);
    });
    it('allows to select items using mouse', async () => {
      const onChange = jest.fn();
      const { driver } = createDriver(
        <TestPagination
          totalPages={3}
          currentPage={2}
          onChange={onChange}
          pageUrl={(page) => `${page}`}
        />,
      );
      await driver.clickNextButton();
      await driver.clickPreviousButton();
      expect(onChange).toHaveBeenCalledTimes(2);
    });
  });

  it('adds IDs to the elements if ID prefix is provided', async () => {
    const { driver } = createDriver(
      <TestPagination id="$" totalPages={3} showFirstLastNavButtons />,
    );

    const { firstButton, previousButton, nextButton, lastButton } =
      driver.getNavButtons();
    const pageStrip = driver.getPageStrip();

    expect(await firstButton.attr('id')).toBe('$navButtonFirst');
    expect(await previousButton.attr('id')).toBe('$navButtonPrevious');
    expect(await nextButton.attr('id')).toBe('$navButtonNext');
    expect(await lastButton.attr('id')).toBe('$navButtonLast');
    expect(await pageStrip.attr('id')).toBe('$pageStrip');
  });
  it('does not add ID to the root if ID prefix is not provided', async () => {
    const { driver } = createDriver(<TestPagination totalPages={3} />);
    const element = await driver.element();
    expect(element.hasAttribute('id')).toBe(false);
  });
  it('allows to customize the gap text', async () => {
    const { driver } = createDriver(
      <TestPagination
        totalPages={5}
        maxPagesToShow={4}
        currentPage={0}
        showLastPage
        gapLabel={<em>*</em>}
      />,
    );

    expect(await driver.getPageLabels()).toEqual(['1', '2', '*', '5']);
  });
  it('adds URLs to the pages according to desired format', async () => {
    const { driver } = createDriver(
      <TestPagination
        totalPages={3}
        currentPage={1}
        pageUrl={(n) => `https://example.com/${n}/`}
        showFirstLastNavButtons
      />,
    );

    const { firstButton, previousButton, nextButton, lastButton } =
      driver.getNavButtons();
    expect(await firstButton.attr('href')).toBe(null);
    expect(await previousButton.attr('href')).toBe(null);
    expect(await nextButton.attr('href')).toEqual('https://example.com/2/');
    expect(await lastButton.attr('href')).toEqual('https://example.com/3/');
    expect(await driver.getPageByNumber(1).attr('href')).toEqual(null);
    expect(await driver.getPageByNumber(2).attr('href')).toEqual(
      'https://example.com/2/',
    );
    expect(await driver.getPageByNumber(3).attr('href')).toEqual(
      'https://example.com/3/',
    );
  });
  it('calls onClick when clicking on the component', async () => {
    const onClick = jest.fn();
    const { driver } = createDriver(
      <TestPagination totalPages={3} onClick={onClick} />,
    );
    await driver.click();
    expect(onClick).toHaveBeenCalled();
  });
  it('calls onDoubleClick when double clicking on the component', async () => {
    const onDoubleClick = jest.fn();
    const { driver } = createDriver(
      <TestPagination totalPages={3} onDoubleClick={onDoubleClick} />,
    );
    await driver.dblClick();
    expect(onDoubleClick).toHaveBeenCalled();
  });
});
